import InfoTooltip from '@/components/tooltip/InfoTooltip';
import clsx from 'clsx';
import { FC, useEffect } from 'react';
import { FieldError, useFormContext } from 'react-hook-form';
import { get } from '../../../utils';
import { DefaultError } from './DefaultError';
import FieldLabel from './FieldLabel';

export interface InputProps<T> {
  id: string;
  value: T;
  onChange: (value: T) => void;
  onBlur?: (value: T) => void;
  readonly?: boolean;
  className?: string;
  autofocus?: boolean;
}

interface Props {
  id: string;
  description?: string;
  label?: string;
  optional?: boolean;
  example?: string;
  readonly?: boolean;
  className?: string;
  overrideValue?: any;
  onChangeCallback?: (value: any) => void;
  tooltipText?: string;
}

type FieldProps<A> = A & Props;

export function withField<T, A>(Input: FC<InputProps<T> & A>, Error: FC<FieldError> = DefaultError): FC<FieldProps<A>> {
  const Field: FC<FieldProps<A>> = props => {
    const { register, unregister, watch, formState, setValue } = useFormContext();
    const {
      id,
      label,
      description,
      className,
      optional,
      example,
      readonly,
      overrideValue,
      onChangeCallback,
      tooltipText
    } = props;

    useEffect(() => {
      register(id);
    }, [id, register, unregister]);

    const value = (overrideValue || watch(id)) as T;
    const error = get(id, formState.errors) as FieldError | undefined;

    return (
      <div className={clsx('flex flex-col gap-0.5', className)}>
        <div className="flex items-center space-x-2">
          {label && <FieldLabel id={id} label={label} optional={optional} />}
          {tooltipText && <InfoTooltip id={id} text={tooltipText} />}
        </div>
        {description && <p className="-mt-1 mb-1 text-sm leading-tight">{description}</p>}
        <Input
          {...props}
          readonly={readonly}
          value={value}
          onChange={v => {
            setValue(id, v, { shouldDirty: true });
            onChangeCallback && onChangeCallback(v);
          }}
          className={readonly ? 'pointer-events-none bg-gray-200' : ''}
        />
        {example && <p className="text-secondary-std block text-xs -mt-1 leading-6">{example}</p>}
        {error && <Error {...error} />}
      </div>
    );
  };
  return Field;
}
